import React, { useEffect, useState, useRef } from 'react';
import { KnowledgeSpaceCard } from './KnowledgeSpaceCard';
import { ChatbotOverviewCard } from './chatbots/ChatbotOverviewCard';
import { PlusIcon, SearchIcon, ActivityIcon, ServerIcon, CpuIcon, DatabaseIcon, XIcon, FilterIcon, SortAscIcon, LayoutGridIcon, LayoutListIcon, DollarSignIcon, InfoIcon } from 'lucide-react';
import { CreateKnowledgeSpaceModal } from './knowledge-space/CreateKnowledgeSpaceModal';
import { LoadingSpinner } from './common/LoadingSpinner';
import { KnowledgeSpace } from '../types';
import { useClient } from '../contexts/ClientContext';
import { KnowledgeSpaceCardSkeleton } from './common/Skeleton';
import { useUsageTracking } from '../hooks/useUsageTracking';
import { AlertCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { QuotaWarning } from './common/QuotaWarning';
// Mock data
const systemStatus = {
  api: {
    status: 'healthy',
    latency: '45ms'
  },
  database: {
    status: 'healthy',
    connections: 28
  },
  compute: {
    status: 'healthy',
    load: '67%'
  },
  vectorStore: {
    status: 'healthy',
    indices: 12
  }
};
const mockKnowledgeSpaces = [{
  id: '1',
  name: 'ADHD Research Space',
  chatbotCount: 3,
  storageUsed: '1.2 GB',
  documentsCount: 45,
  createdAt: '2025-04-28T10:30:00Z',
  updatedAt: '2025-04-28T10:30:00Z',
  description: 'AI-powered research assistant for ADHD studies and papers',
  status: 'active' as const,
  ownerId: '1'
}, {
  id: '2',
  name: 'Customer Support Knowledge Base',
  chatbotCount: 2,
  storageUsed: '800 MB',
  documentsCount: 30,
  createdAt: '2025-04-28T09:30:00Z',
  updatedAt: '2025-04-28T09:30:00Z',
  description: 'Centralized knowledge base for customer support team',
  status: 'active' as const,
  ownerId: '1'
}];
export const Dashboard = () => {
  const [mockChatbots, setMockChatbots] = useState([{
    id: '1',
    name: 'Research Assistant',
    description: 'AI assistant for research analysis and synthesis',
    connectedSpaces: 3,
    totalInteractions: 1250,
    status: 'active' as const,
    type: 'researcher' as const,
    lastActive: '2025-04-28T10:30:00Z',
    averageResponseTime: '1.2s',
    successRate: 98.5,
    activeUsers: 156
  }, {
    id: '2',
    name: 'Customer Support Bot',
    description: 'Handles customer inquiries and support requests',
    connectedSpaces: 2,
    totalInteractions: 3420,
    status: 'active' as const,
    type: 'assistant' as const,
    lastActive: '2025-04-28T11:15:00Z',
    averageResponseTime: '0.8s',
    successRate: 99.2,
    activeUsers: 342
  }]);
  const {
    currentClient
  } = useClient();
  const {
    knowledgeSpaces: spaceQuota,
    checkQuota,
    refreshUsage
  } = useUsageTracking();
  // Initialize all state at the top of the component
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentChatbotPage, setCurrentChatbotPage] = useState(1);
  const [chatbotQuota, setChatbotQuota] = useState({
    current: 0,
    limit: 10,
    isExceeded: false
  });
  const [showCreateChatbotModal, setShowCreateChatbotModal] = useState(false);
  // Move mock data into state
  const [knowledgeSpaces, setKnowledgeSpaces] = useState([{
    id: '1',
    name: 'ADHD Research Space',
    chatbotCount: 3,
    storageUsed: '1.2 GB',
    documentsCount: 45,
    createdAt: '2025-04-28T10:30:00Z',
    updatedAt: '2025-04-28T10:30:00Z',
    description: 'AI-powered research assistant for ADHD studies and papers',
    status: 'active' as const,
    ownerId: '1'
  }, {
    id: '2',
    name: 'Customer Support Knowledge Base',
    chatbotCount: 2,
    storageUsed: '800 MB',
    documentsCount: 30,
    createdAt: '2025-04-28T09:30:00Z',
    updatedAt: '2025-04-28T09:30:00Z',
    description: 'Centralized knowledge base for customer support team',
    status: 'active' as const,
    ownerId: '1'
  }]);
  // Add useEffect to simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  // Calculate filtered spaces after state initialization
  const filteredSpaces = knowledgeSpaces.filter(space => {
    const matchesSearch = space.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes('has-chatbots') && space.chatbotCount > 0 || selectedFilters.includes('has-documents') && space.documentsCount > 0;
    return matchesSearch && matchesFilters;
  }).sort((a, b) => {
    const aValue = sortBy === 'date' ? new Date(a.createdAt).getTime() : a.name;
    const bValue = sortBy === 'date' ? new Date(b.createdAt).getTime() : b.name;
    const modifier = sortOrder === 'asc' ? 1 : -1;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * modifier;
    }
    return ((aValue as number) - (bValue as number)) * modifier;
  });
  // Pagination state
  const itemsPerPage = 6; // Show 6 cards per page
  // Calculate pagination
  const totalPages = Math.ceil(filteredSpaces.length / itemsPerPage);
  const paginatedSpaces = filteredSpaces.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Render knowledge spaces with pagination
  const renderKnowledgeSpaces = () => {
    if (isLoading) {
      return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-452">
          {[...Array(3)].map((_, i) => <KnowledgeSpaceCardSkeleton key={i} data-id="element-453" />)}
        </div>;
    }
    if (filteredSpaces.length === 0) {
      return <div className="bg-surface-light border border-surface-border rounded-none p-8 text-center" data-id="element-454">
          <p className="text-gray-400" data-id="element-455">No knowledge spaces found</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-456">
            Create your first knowledge space to get started
          </p>
        </div>;
    }
    return <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-457">
          {paginatedSpaces.map(space => <KnowledgeSpaceCard key={space.id} id={space.id} name={space.name} chatbotCount={space.chatbotCount} storageUsed={space.storageUsed} documentsCount={space.documentsCount} createdAt={space.createdAt} updatedAt={space.updatedAt} description={space.description} status={space.status} data-id="element-458" />)}
        </div>
        {/* Pagination Controls */}
        {filteredSpaces.length > itemsPerPage && <div className="mt-6 flex items-center justify-between border-t border-surface-border pt-6" data-id="element-459">
            <p className="text-sm text-gray-400" data-id="element-460">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredSpaces.length)} of{' '}
              {filteredSpaces.length} spaces
            </p>
            <div className="flex items-center gap-2" data-id="element-461">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 border border-surface-border rounded-none text-gray-400 
                         hover:text-white disabled:opacity-50 transition-colors" data-id="element-462">
                <ChevronLeftIcon size={16} data-id="element-463" />
              </button>
              {[...Array(totalPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`w-8 h-8 font-mono text-sm border rounded-none transition-colors
                    ${currentPage === i + 1 ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}`} data-id="element-464">
                  {i + 1}
                </button>)}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 border border-surface-border rounded-none text-gray-400 
                         hover:text-white disabled:opacity-50 transition-colors" data-id="element-465">
                <ChevronRightIcon size={16} data-id="element-466" />
              </button>
            </div>
          </div>}
      </>;
  };
  // Pagination state for chatbots
  const chatbotsPerPage = 6;
  // Calculate chatbot pagination
  const totalChatbotPages = Math.ceil(mockChatbots.length / chatbotsPerPage);
  const paginatedChatbots = mockChatbots.slice((currentChatbotPage - 1) * chatbotsPerPage, currentChatbotPage * chatbotsPerPage);
  // Render chatbots with pagination
  const renderChatbots = () => {
    if (isLoading) {
      return <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-467">
          {[...Array(3)].map((_, i) => <KnowledgeSpaceCardSkeleton key={i} data-id="element-468" />)}
        </div>;
    }
    if (mockChatbots.length === 0) {
      return <div className="bg-surface-light border border-surface-border rounded-none p-8 text-center" data-id="element-469">
          <p className="text-gray-400" data-id="element-470">No chatbots found</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-471">
            Create your first chatbot to get started
          </p>
        </div>;
    }
    return <>
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-472">
          {paginatedChatbots.map(chatbot => <ChatbotOverviewCard key={chatbot.id} id={chatbot.id} name={chatbot.name} description={chatbot.description} connectedSpaces={chatbot.connectedSpaces} totalInteractions={chatbot.totalInteractions} status={chatbot.status} type={chatbot.type} lastActive={chatbot.lastActive} averageResponseTime={chatbot.averageResponseTime} successRate={chatbot.successRate} activeUsers={chatbot.activeUsers} data-id="element-473" />)}
        </div>
        {/* Chatbot Pagination Controls */}
        {mockChatbots.length > chatbotsPerPage && <div className="mt-6 flex items-center justify-between border-t border-surface-border pt-6" data-id="element-474">
            <p className="text-sm text-gray-400" data-id="element-475">
              Showing {(currentChatbotPage - 1) * chatbotsPerPage + 1} to{' '}
              {Math.min(currentChatbotPage * chatbotsPerPage, mockChatbots.length)}{' '}
              of {mockChatbots.length} chatbots
            </p>
            <div className="flex items-center gap-2" data-id="element-476">
              <button onClick={() => setCurrentChatbotPage(prev => Math.max(prev - 1, 1))} disabled={currentChatbotPage === 1} className="p-2 border border-surface-border rounded-none text-gray-400 
                         hover:text-white disabled:opacity-50 transition-colors" data-id="element-477">
                <ChevronLeftIcon size={16} data-id="element-478" />
              </button>
              {[...Array(totalChatbotPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentChatbotPage(i + 1)} className={`w-8 h-8 font-mono text-sm border rounded-none transition-colors
                    ${currentChatbotPage === i + 1 ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}`} data-id="element-479">
                  {i + 1}
                </button>)}
              <button onClick={() => setCurrentChatbotPage(prev => Math.min(prev + 1, totalChatbotPages))} disabled={currentChatbotPage === totalChatbotPages} className="p-2 border border-surface-border rounded-none text-gray-400 
                         hover:text-white disabled:opacity-50 transition-colors" data-id="element-480">
                <ChevronRightIcon size={16} data-id="element-481" />
              </button>
            </div>
          </div>}
      </>;
  };
  // Add search and filter functions
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  const handleViewModeToggle = (mode: 'grid' | 'list') => {
    setViewMode(mode);
  };
  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };
  const handleSortOrderToggle = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };
  const handleCreateSpace = async (data: Partial<KnowledgeSpace>) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Knowledge space created successfully');
      setShowCreateModal(false);
    } catch (error) {
      toast.error('Failed to create knowledge space');
    }
  };
  return <div className="w-full" data-id="element-482">
      <main className="container mx-auto px-4 py-8 max-w-7xl" data-id="element-483">
        <div className="flex flex-col gap-6" data-id="element-484">
          {/* Fix QuotaWarning usage by providing required props */}
          {spaceQuota.percentageUsed >= 90 && <QuotaWarning quotaType="knowledgeSpaces" quota={spaceQuota} data-id="element-485" />}
          {/* Add billing estimate card */}
          <div className="bg-surface-light border border-surface-border p-4 rounded-none" data-id="element-486">
            <div className="flex items-start gap-4" data-id="element-487">
              <div className="p-2 bg-surface rounded-none border border-surface-border" data-id="element-488">
                <DollarSignIcon size={18} className="text-accent-light" data-id="element-489" />
              </div>
              <div data-id="element-490">
                <div className="flex items-center gap-2" data-id="element-491">
                  <h2 className="text-lg font-medium text-white font-mono" data-id="element-492">
                    Your Estimated Billing
                  </h2>
                  <div className="relative group" data-id="element-493">
                    <InfoIcon size={16} className="text-gray-400 cursor-help" data-id="element-494" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-surface-light border border-surface-border 
                                  text-xs text-gray-300 rounded-none opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-495">
                      This estimate is based on your current usage and may
                      change.
                    </div>
                  </div>
                </div>
                <p className="text-2xl font-mono text-accent-light mt-1" data-id="element-496">
                  $48.00
                </p>
                <p className="text-sm text-gray-400 font-mono mt-1" data-id="element-497">
                  Current monthly estimate
                </p>
              </div>
            </div>
          </div>
          {/* Knowledge Spaces Section */}
          <div className="space-y-6" data-id="element-498">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-499">
              <div data-id="element-500">
                <h2 className="text-2xl font-medium text-white font-mono mb-1" data-id="element-501">
                  Knowledge Spaces
                </h2>
                <p className="text-gray-400 font-mono" data-id="element-502">
                  {spaceQuota.current} / {spaceQuota.limit} Spaces Used
                </p>
              </div>
              <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light 
                       text-white rounded-none transition-colors font-mono text-sm 
                       uppercase tracking-wide w-full sm:w-auto" data-id="element-503">
                <PlusIcon size={18} data-id="element-504" />
                <span data-id="element-505">New Space</span>
              </button>
            </div>
            {/* Search and Filters */}
            <div className="bg-surface-light border border-surface-border p-4 rounded-none" data-id="element-506">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-507">
                <div className="flex-1 relative group" data-id="element-508">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 
                               group-focus-within:text-accent transition-colors" data-id="element-509" />
                  <input type="text" value={searchQuery} onChange={handleSearch} placeholder="Search knowledge spaces..." className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                               text-white placeholder-gray-400 focus:outline-none focus:ring-1 
                               focus:ring-accent focus:border-accent transition-all font-mono" data-id="element-510" />
                  {searchQuery && <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 
                               hover:text-gray-300 transition-colors" data-id="element-511">
                      <XIcon size={14} data-id="element-512" />
                    </button>}
                </div>
                <div className="flex items-center gap-3" data-id="element-513">
                  <button onClick={handleFilterToggle} className={`flex items-center gap-2 px-4 py-2 border rounded-none font-mono text-sm
                      ${showFilters ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-300 hover:text-white'}`} data-id="element-514">
                    <FilterIcon size={16} data-id="element-515" />
                    <span data-id="element-516">Filters</span>
                  </button>
                  <button onClick={handleSortOrderToggle} className="p-2 border border-surface-border rounded-none text-gray-400 
                               hover:text-white transition-colors" data-id="element-517">
                    <SortAscIcon size={16} className={`transform transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} data-id="element-518" />
                  </button>
                  <div className="h-6 border-l border-surface-border" data-id="element-519" />
                  <button onClick={() => handleViewModeToggle('grid')} className={`p-2 rounded-none transition-colors ${viewMode === 'grid' ? 'text-accent' : 'text-gray-400 hover:text-white'}`} data-id="element-520">
                    <LayoutGridIcon size={16} data-id="element-521" />
                  </button>
                  <button onClick={() => handleViewModeToggle('list')} className={`p-2 rounded-none transition-colors ${viewMode === 'list' ? 'text-accent' : 'text-gray-400 hover:text-white'}`} data-id="element-522">
                    <LayoutListIcon size={16} data-id="element-523" />
                  </button>
                </div>
              </div>
              {/* Filter Options */}
              {showFilters && <div className="mt-4 pt-4 border-t border-surface-border animate-in slide-in-from-top-2" data-id="element-524">
                  <div className="flex flex-wrap gap-2" data-id="element-525">
                    <button onClick={() => setSelectedFilters(prev => prev.includes('has-chatbots') ? prev.filter(f => f !== 'has-chatbots') : [...prev, 'has-chatbots'])} className={`px-3 py-1.5 border rounded-none text-sm font-mono transition-colors
                        ${selectedFilters.includes('has-chatbots') ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}`} data-id="element-526">
                      Has Chatbots
                    </button>
                    <button onClick={() => setSelectedFilters(prev => prev.includes('has-documents') ? prev.filter(f => f !== 'has-documents') : [...prev, 'has-documents'])} className={`px-3 py-1.5 border rounded-none text-sm font-mono transition-colors
                        ${selectedFilters.includes('has-documents') ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}`} data-id="element-527">
                      Has Documents
                    </button>
                  </div>
                </div>}
            </div>
            {/* Knowledge Spaces Grid */}
            {renderKnowledgeSpaces()}
          </div>
          {/* Chatbots Section */}
          <div className="mt-12" data-id="element-528">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-529">
              <div data-id="element-530">
                <h2 className="text-2xl font-medium text-white font-mono mb-1" data-id="element-531">
                  Chatbots
                </h2>
                <p className="text-gray-400 font-mono" data-id="element-532">
                  {chatbotQuota.current} / {chatbotQuota.limit} Chatbots Used
                </p>
              </div>
              <button onClick={() => setShowCreateChatbotModal(true)} disabled={chatbotQuota.isExceeded || isLoading} className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light 
               text-white rounded-none transition-colors font-mono text-sm 
               uppercase tracking-wide w-full sm:w-auto" data-id="element-533">
                <PlusIcon size={18} data-id="element-534" />
                <span data-id="element-535">New Chatbot</span>
              </button>
            </div>
            {/* Search and Filters */}
            <div className="bg-surface-light border border-surface-border p-4 rounded-none mb-6" data-id="element-536">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-537">
                <div className="flex-1 relative group" data-id="element-538">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 
                               group-focus-within:text-accent transition-colors" data-id="element-539" />
                  <input type="text" value={searchQuery} onChange={handleSearch} placeholder="Search chatbots..." className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                               text-white placeholder-gray-400 focus:outline-none focus:ring-1 
                               focus:ring-accent focus:border-accent transition-all font-mono" data-id="element-540" />
                </div>
                <div className="flex items-center gap-3" data-id="element-541">
                  <button onClick={() => handleViewModeToggle('grid')} className={`p-2 rounded-none transition-colors ${viewMode === 'grid' ? 'text-accent' : 'text-gray-400 hover:text-white'}`} data-id="element-542">
                    <LayoutGridIcon size={16} data-id="element-543" />
                  </button>
                  <button onClick={() => handleViewModeToggle('list')} className={`p-2 rounded-none transition-colors ${viewMode === 'list' ? 'text-accent' : 'text-gray-400 hover:text-white'}`} data-id="element-544">
                    <LayoutListIcon size={16} data-id="element-545" />
                  </button>
                </div>
              </div>
            </div>
            {/* Chatbots Grid with Pagination */}
            {renderChatbots()}
          </div>
          {/* System Status Bar */}
          <div className="bg-surface-light border border-surface-border p-4 rounded-none overflow-x-auto" data-id="element-546">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 min-w-[500px]" data-id="element-547">
              <div className="flex items-center gap-2" data-id="element-548">
                <ActivityIcon size={16} className="text-status-success animate-pulse shrink-0" data-id="element-549" />
                <span className="text-xs text-gray-400 font-mono whitespace-nowrap" data-id="element-550">
                  API: {systemStatus.api.latency}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-551">
                <DatabaseIcon size={16} className="text-status-success shrink-0" data-id="element-552" />
                <span className="text-xs text-gray-400 font-mono whitespace-nowrap" data-id="element-553">
                  DB: {systemStatus.database.connections} connections
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-554">
                <CpuIcon size={16} className="text-status-success shrink-0" data-id="element-555" />
                <span className="text-xs text-gray-400 font-mono whitespace-nowrap" data-id="element-556">
                  Load: {systemStatus.compute.load}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-557">
                <ServerIcon size={16} className="text-status-success shrink-0" data-id="element-558" />
                <span className="text-xs text-gray-400 font-mono whitespace-nowrap" data-id="element-559">
                  Vectors: {systemStatus.vectorStore.indices} indices
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Existing modals */}
      <CreateKnowledgeSpaceModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSave={handleCreateSpace} data-id="element-560" />
      <CreateKnowledgeSpaceModal isOpen={showCreateChatbotModal} onClose={() => setShowCreateChatbotModal(false)} onSave={() => {}} data-id="element-561" />
    </div>;
};